import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import {
    FikoSmartPoint3D,
    SmartPoint3DCategory,
    FikoSmartPoint3DConfig,
    LeaderTip,
    GDTTip,
    YuzeyKaliteTip,
    KaynakTip,
    YonTip,
    DuzlemTip
} from '../../IFikoThreeJS/FikoSmartPoint3D';
import * as THREE from 'three';

/**
 * Smart Point Dialog - CSS3D Renderer versiyonu
 * Doğru derinlik sıralaması ile
 */
@Component({
    selector: 'app-fiko-smart-point',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './fiko-smart-point.component.html',
    styleUrls: ['./fiko-smart-point.component.css']
})
export class FikoSmartPointComponent implements OnInit, OnDestroy {
    @Input() anaGosterim: FikoThreeJsComponent;

    private _pencere: any;
    @Input()
    set pencere(value: any) {
        this._pencere = value;
        // Dialog her açıldığında/güncellendiğinde bekleyen düzenleme isteğini kontrol et
        if (value) {
            setTimeout(() => this.checkPendingEdit(), 50);
        }
    }
    get pencere(): any {
        return this._pencere;
    }

    // Temel ayarlar
    baslik: string = 'Nokta';
    aciklama: string = '';
    kategori: SmartPoint3DCategory = 'info';
    customIkon: string = '';  // Kullanıcı tanımlı ikon (emoji, numara, yazı)
    ozelRenk: string = '';    // Kullanıcı tanımlı nokta rengi (boş = kategori rengi)
    yaziRenk: string = '';    // Kullanıcı tanımlı yazı rengi (boş = beyaz)
    etiketGorunsun: boolean = false;
    sabitBoyut: boolean = true;
    animasyonAktif: boolean = true;  // Halka animasyonu
    boyut: number = 48; // Nokta boyutu (referans: 48, aralık: 12-120)
    pencereBoyut: number = 100; // Pencere boyutu (referans: 100, aralık: 50-200)

    // Durum
    noktaPozisyonu: THREE.Vector3 | null = null;
    secimModu: boolean = false;
    private mevcutNokta: FikoSmartPoint3D | null = null;

    // ═══════════════════════════════════════════════════════════════════════
    // PROFESYONEL CAD ÖZELLİKLERİ
    // ═══════════════════════════════════════════════════════════════════════

    // PRO-only mod - İkon gizlenir, sadece PRO badge'ler görünür
    proOnlyMod: boolean = false;

    // Leader Line - TAM ÖZELLEŞTİRİLEBİLİR
    leaderAktif: boolean = false;
    leaderTip: LeaderTip = 'straight';
    leaderRenk: string = '#1A1A1A';
    leaderKalinlik: number = 1.5;
    leaderYon: YonTip = 'sol';           // 8 yön desteği
    leaderUzunluk: number = 5;           // Çizgi uzunluğu
    leaderOffsetX: number = 0;           // X offset
    leaderOffsetY: number = 0;           // Y offset

    // GD&T (Geometric Dimensioning & Tolerancing) - TAM ÖZELLEŞTİRİLEBİLİR
    gdtAktif: boolean = false;
    gdtTip: GDTTip = 'position';
    gdtDeger: string = '';
    gdtDatum: string = '';
    gdtMalzemeKosulu: '' | 'M' | 'L' | 'S' = '';
    gdtSeffafArkaplan: boolean = true;   // Şeffaf arka plan
    gdtOffsetX: number = 3;              // X offset
    gdtOffsetY: number = 1.5;            // Y offset
    gdtDuzlem: DuzlemTip = 'XY';         // 3D düzlem (gerçek 3D)

    // Yüzey Kalitesi - TAM ÖZELLEŞTİRİLEBİLİR
    yuzeyKaliteAktif: boolean = false;
    yuzeyKaliteTip: YuzeyKaliteTip = 'machined';
    yuzeyRa: number = 3.2;
    yuzeySeffafArkaplan: boolean = true;  // Şeffaf arka plan
    yuzeyOffsetX: number = 2.5;           // X offset
    yuzeyOffsetY: number = -1.2;          // Y offset
    yuzeyDuzlem: DuzlemTip = 'XY';        // 3D düzlem (gerçek 3D)

    // Kaynak Sembolü - TAM ÖZELLEŞTİRİLEBİLİR
    kaynakAktif: boolean = false;
    kaynakTip: KaynakTip = 'fillet';
    kaynakBoyut: string = '';
    kaynakTamCevre: boolean = false;
    kaynakSahada: boolean = false;
    kaynakSeffafArkaplan: boolean = true;  // Şeffaf arka plan
    kaynakOffsetX: number = -2.5;          // X offset
    kaynakOffsetY: number = 1.2;           // Y offset
    kaynakDuzlem: DuzlemTip = 'XY';        // 3D düzlem (gerçek 3D)

    // Datum Marker - TAM ÖZELLEŞTİRİLEBİLİR
    datumAktif: boolean = false;
    datumTip: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'custom' = 'A';
    datumCustomLabel: string = '';
    datumRenk: string = '#1A1A1A';
    datumUcgenGoster: boolean = true;
    datumSeffafArkaplan: boolean = true;  // Şeffaf arka plan
    datumOffsetX: number = -4;            // X offset
    datumOffsetY: number = 0;             // Y offset
    datumDuzlem: DuzlemTip = 'XY';        // 3D düzlem (gerçek 3D)

    // Dimension Line (Ölçü Çizgisi) - TAM ÖZELLEŞTİRİLEBİLİR
    dimensionAktif: boolean = false;
    dimensionTip: 'linear' | 'diameter' | 'radius' | 'angle' = 'linear';
    dimensionDeger: string = '';
    dimensionToleransUst: string = '';
    dimensionToleransAlt: string = '';
    dimensionBirim: string = 'mm';
    dimensionOkTip: 'arrow' | 'dot' | 'slash' | 'none' = 'arrow';
    dimensionSeffafArkaplan: boolean = true;  // Şeffaf arka plan
    dimensionOffsetX: number = 0;             // X offset
    dimensionOffsetY: number = -3;            // Y offset
    dimensionUzunluk: number = 8;             // Çizgi uzunluğu
    dimensionDuzlem: DuzlemTip = 'XY';        // 3D düzlem (gerçek 3D)

    // Yön seçenekleri
    yonSecenekleri: { value: YonTip; label: string }[] = [
        { value: 'sol', label: '← Sol' },
        { value: 'sag', label: '→ Sağ' },
        { value: 'ust', label: '↑ Üst' },
        { value: 'alt', label: '↓ Alt' },
        { value: 'solUst', label: '↖ Sol Üst' },
        { value: 'sagUst', label: '↗ Sağ Üst' },
        { value: 'solAlt', label: '↙ Sol Alt' },
        { value: 'sagAlt', label: '↘ Sağ Alt' }
    ];

    // 3D Düzlem seçenekleri (gerçek 3D mesh için)
    duzlemSecenekleri: { value: DuzlemTip; label: string }[] = [
        { value: 'XY', label: 'XY (Ön)' },
        { value: 'XZ', label: 'XZ (Üst)' },
        { value: 'YZ', label: 'YZ (Yan)' }
    ];

    // Düzenleme modu
    duzenlemeModu: boolean = false;
    private duzenlenenNokta: FikoSmartPoint3D | null = null;

    // Kamera konumu durumu
    kameraKonumuVar: boolean = false;

    // Kategoriler
    kategoriler: { value: SmartPoint3DCategory; label: string; ikon: string; renk: string }[] = [
        { value: 'info', label: 'Bilgi', ikon: 'ℹ️', renk: '#2196F3' },
        { value: 'warning', label: 'Uyarı', ikon: '⚠️', renk: '#FF9800' },
        { value: 'error', label: 'Hata', ikon: '❌', renk: '#F44336' },
        { value: 'success', label: 'Onay', ikon: '✓', renk: '#4CAF50' },
        { value: 'measure', label: 'Ölçüm', ikon: '📏', renk: '#9C27B0' },
        { value: 'reference', label: 'Referans', ikon: '📍', renk: '#00BCD4' },
        { value: 'custom', label: 'Özel', ikon: '●', renk: '#607D8B' }
    ];

    constructor(
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone
    ) {}

    get eklemeAktif(): boolean {
        return this.noktaPozisyonu !== null && this.baslik.trim().length > 0;
    }

    get secilenKategori() {
        return this.kategoriler.find(k => k.value === this.kategori);
    }

    // Aktif renk: özel renk varsa onu, yoksa kategori rengi
    get aktifRenk(): string {
        return this.ozelRenk?.trim() || this.secilenKategori?.renk || '#2196F3';
    }

    ngOnInit(): void {
        // İlk açılışta bekleyen düzenleme isteği varsa kontrol et
        // (pencere setter'ı da kontrol ediyor ama ilk açılışta setter çağrılmayabilir)
    }

    /**
     * Bekleyen düzenleme isteğini kontrol et
     * Dialog açıldığında sağ tık ile düzenlenecek nokta varsa düzenleme moduna geç
     */
    private checkPendingEdit(): void {
        if (this.anaGosterim?.pendingSmartPointEdit) {
            const nokta = this.anaGosterim.pendingSmartPointEdit;
            // Pending'i temizle
            this.anaGosterim.pendingSmartPointEdit = null;
            // Düzenleme modunu başlat
            setTimeout(() => {
                this.duzenlemeBaslat(nokta);
            }, 50);
        }
    }

    ngOnDestroy(): void {
        if (this.secimModu) {
            this.secimModunuKapat();
        }
        this.onizlemeyiTemizle();
    }

    secKategori(kat: SmartPoint3DCategory): void {
        this.kategori = kat;
        this.onizlemeGuncelle();
    }

    pozisyonSec(): void {
        this.secimModu = true;
        const canvas = this.anaGosterim?.sunucu?.domElement;
        if (canvas) {
            canvas.style.cursor = 'crosshair';
            canvas.addEventListener('click', this.noktaTiklama);
        }
    }

    private noktaTiklama = (event: MouseEvent) => {
        const position = this.get3DPositionFromMouse(event);
        if (!position) return;

        this.ngZone.run(() => {
            this.noktaPozisyonu = position;
            this.secimModunuKapat();

            // Düzenleme modunda noktanın pozisyonunu da güncelle
            if (this.duzenlemeModu && this.duzenlenenNokta) {
                this.duzenlenenNokta.setPosition(position);
            }

            this.onizlemeGuncelle();
            this.cdr.detectChanges();
        });
    };

    private secimModunuKapat(): void {
        this.secimModu = false;
        const canvas = this.anaGosterim?.sunucu?.domElement;
        if (canvas) {
            canvas.style.cursor = 'default';
            canvas.removeEventListener('click', this.noktaTiklama);
        }
    }

    private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
        if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

        const canvas = this.anaGosterim.sunucu.domElement;
        const rect = canvas.getBoundingClientRect();

        const mouse = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

        const sahne = this.anaGosterim.sahneler?.[0];
        if (!sahne) return null;

        const meshes: THREE.Mesh[] = [];
        sahne.traverse((obj: THREE.Object3D) => {
            if (obj instanceof THREE.Mesh && obj.geometry && obj.visible) {
                if (!obj.name.includes('Helper') && !obj.name.includes('SmartPoint')) {
                    meshes.push(obj);
                }
            }
        });

        const intersects = raycaster.intersectObjects(meshes, true);
        if (intersects.length > 0) {
            return intersects[0].point.clone();
        }

        // Fallback - plane intersection
        const cameraDir = new THREE.Vector3();
        this.anaGosterim.aktifKamera.getWorldDirection(cameraDir);
        const plane = new THREE.Plane(cameraDir.negate(), this.anaGosterim.aktifKamera.position.length() * 0.5);
        const intersection = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(plane, intersection)) {
            return intersection;
        }
        return null;
    }

    onizlemeGuncelle(): void {
        // Profesyonel CAD config objesi oluştur
        const proConfig = this.buildProConfig();

        // Düzenleme modunda önizleme güncellemesi - direkt noktayı güncelle
        if (this.duzenlemeModu && this.duzenlenenNokta) {
            this.duzenlenenNokta.updateConfig({
                baslik: this.baslik,
                aciklama: this.aciklama,
                kategori: this.kategori,
                customIkon: this.customIkon,
                ozelRenk: this.ozelRenk,
                yaziRenk: this.yaziRenk,
                boyut: this.boyut,
                pencereBoyut: this.pencereBoyut,
                sabitBoyut: this.sabitBoyut,
                etiketGorunsun: this.etiketGorunsun,
                animasyonAktif: this.animasyonAktif,
                ...proConfig
            });
            this.anaGosterim?.ciz();
            return;
        }

        if (!this.noktaPozisyonu) return;

        const sahne = this.anaGosterim?.sahneler?.[0];
        const css3dSahne = this.anaGosterim?.css3dSahne;
        if (!sahne || !css3dSahne) return;

        this.onizlemeyiTemizle();

        this.mevcutNokta = new FikoSmartPoint3D({
            baslik: this.baslik,
            aciklama: this.aciklama,
            kategori: this.kategori,
            customIkon: this.customIkon,
            ozelRenk: this.ozelRenk,
            yaziRenk: this.yaziRenk,
            etiketGorunsun: this.etiketGorunsun,
            sabitBoyut: this.sabitBoyut,
            animasyonAktif: this.animasyonAktif,
            boyut: this.boyut,
            pencereBoyut: this.pencereBoyut,
            ...proConfig
        });

        this.mevcutNokta.init();
        this.mevcutNokta.setPosition(this.noktaPozisyonu);
        this.mevcutNokta.hazirla(sahne, css3dSahne);

        this.anaGosterim?.ciz();
    }

    /**
     * Profesyonel CAD config objesi oluştur
     */
    private buildProConfig(): Partial<FikoSmartPoint3DConfig> {
        const config: Partial<FikoSmartPoint3DConfig> = {};

        // PRO-only mod
        config.proOnlyMod = this.proOnlyMod;

        // Leader Line - TAM ÖZELLEŞTİRİLEBİLİR
        if (this.leaderAktif) {
            config.leader = {
                aktif: true,
                tip: this.leaderTip,
                renk: this.leaderRenk,
                kalinlik: this.leaderKalinlik,
                yon: this.leaderYon,
                uzunluk: this.leaderUzunluk,
                offset: { x: this.leaderOffsetX, y: this.leaderOffsetY, z: 0 }
            };
        } else {
            config.leader = { aktif: false };
        }

        // GD&T - TAM ÖZELLEŞTİRİLEBİLİR (GERÇEK 3D MESH)
        if (this.gdtAktif) {
            config.gdt = {
                aktif: true,
                tip: this.gdtTip,
                deger: this.gdtDeger,
                datum: this.gdtDatum,
                malzemeKosulu: this.gdtMalzemeKosulu || undefined,
                seffafArkaplan: this.gdtSeffafArkaplan,
                offset: { x: this.gdtOffsetX, y: this.gdtOffsetY, z: 0 },
                duzlem: this.gdtDuzlem  // 3D düzlem (XY, XZ, YZ)
            };
        } else {
            config.gdt = { aktif: false };
        }

        // Yüzey Kalitesi - TAM ÖZELLEŞTİRİLEBİLİR (GERÇEK 3D MESH)
        if (this.yuzeyKaliteAktif) {
            config.yuzeyKalite = {
                aktif: true,
                tip: this.yuzeyKaliteTip,
                ra: this.yuzeyRa,
                seffafArkaplan: this.yuzeySeffafArkaplan,
                offset: { x: this.yuzeyOffsetX, y: this.yuzeyOffsetY, z: 0 },
                duzlem: this.yuzeyDuzlem  // 3D düzlem (XY, XZ, YZ)
            };
        } else {
            config.yuzeyKalite = { aktif: false };
        }

        // Kaynak Sembolü - TAM ÖZELLEŞTİRİLEBİLİR (GERÇEK 3D MESH)
        if (this.kaynakAktif) {
            config.kaynak = {
                aktif: true,
                tip: this.kaynakTip,
                boyut: this.kaynakBoyut,
                tamCevre: this.kaynakTamCevre,
                sahada: this.kaynakSahada,
                seffafArkaplan: this.kaynakSeffafArkaplan,
                offset: { x: this.kaynakOffsetX, y: this.kaynakOffsetY, z: 0 },
                duzlem: this.kaynakDuzlem  // 3D düzlem (XY, XZ, YZ)
            };
        } else {
            config.kaynak = { aktif: false };
        }

        // Datum Marker - TAM ÖZELLEŞTİRİLEBİLİR (GERÇEK 3D MESH)
        if (this.datumAktif) {
            config.datum = {
                aktif: true,
                tip: this.datumTip,
                customLabel: this.datumCustomLabel,
                renk: this.datumRenk,
                ucgenGoster: this.datumUcgenGoster,
                seffafArkaplan: this.datumSeffafArkaplan,
                offset: { x: this.datumOffsetX, y: this.datumOffsetY, z: 0 },
                duzlem: this.datumDuzlem  // 3D düzlem (XY, XZ, YZ)
            };
        } else {
            config.datum = { aktif: false };
        }

        // Dimension Line - TAM ÖZELLEŞTİRİLEBİLİR (GERÇEK 3D MESH)
        if (this.dimensionAktif) {
            config.dimension = {
                aktif: true,
                tip: this.dimensionTip,
                deger: this.dimensionDeger,
                toleransUst: this.dimensionToleransUst,
                toleransAlt: this.dimensionToleransAlt,
                birim: this.dimensionBirim,
                okTip: this.dimensionOkTip,
                seffafArkaplan: this.dimensionSeffafArkaplan,
                offset: { x: this.dimensionOffsetX, y: this.dimensionOffsetY, z: 0 },
                uzunluk: this.dimensionUzunluk,
                duzlem: this.dimensionDuzlem  // 3D düzlem (XY, XZ, YZ)
            };
        } else {
            config.dimension = { aktif: false };
        }

        return config;
    }

    private onizlemeyiTemizle(): void {
        if (this.mevcutNokta) {
            this.mevcutNokta.dispose();
            this.mevcutNokta = null;
        }
    }

    ekle(): void {
        if (!this.noktaPozisyonu || !this.mevcutNokta) return;

        // Sağ tık düzenleme callback'i bağla
        this.mevcutNokta.setOnEdit((nokta) => {
            this.duzenlemeBaslat(nokta);
        });

        // Nokta CSS3D sahnesinde kalacak, referansı kaydet
        const sahne = this.anaGosterim?.sahneler?.[0];
        if (sahne) {
            // Metadata olarak sakla
            if (!sahne.userData['smartPoints3D']) {
                sahne.userData['smartPoints3D'] = [];
            }
            sahne.userData['smartPoints3D'].push(this.mevcutNokta);

            // FikoEtiketler'e kayıt yap (ürün ağacında görünsün)
            const fikoEtiketler = (sahne as any).fikoEtiketler;
            if (fikoEtiketler?.ekleSmartPoint3D) {
                const proConfig = this.buildProConfig();
                const fikoGrup = fikoEtiketler.ekleSmartPoint3D(this.baslik, {
                    baslik: this.baslik,
                    aciklama: this.aciklama,
                    kategori: this.kategori,
                    pozisyon: {
                        x: this.noktaPozisyonu.x,
                        y: this.noktaPozisyonu.y,
                        z: this.noktaPozisyonu.z
                    },
                    customIkon: this.customIkon,
                    ozelRenk: this.ozelRenk,
                    yaziRenk: this.yaziRenk,
                    boyut: this.boyut,
                    pencereBoyut: this.pencereBoyut,
                    // Profesyonel CAD özellikleri
                    leader: proConfig.leader,
                    gdt: proConfig.gdt,
                    yuzeyKalite: proConfig.yuzeyKalite,
                    kaynak: proConfig.kaynak
                });
                // Karşılıklı referans
                this.mevcutNokta.fikoGrupRef = fikoGrup;
                fikoGrup.userData['smartPoint3D'] = this.mevcutNokta;
            }
        }

        this.anaGosterim?.ciz();
        this.anaGosterim?.FikoYT?.degisti?.emit();

        // Reset - nokta kalıcı olarak eklendi
        this.mevcutNokta = null;
        this.noktaPozisyonu = null;
        this.baslik = 'Nokta';
        this.aciklama = '';
        this.cdr.detectChanges();
    }

    kapat(): void {
        this.onizlemeyiTemizle();
        this.duzenlemeyiIptal();
        const dialogInstance = this.pencere?.instance ?? this.pencere;
        if (dialogInstance?.kapa) {
            dialogInstance.kapa();
        } else if (dialogInstance?.kapat) {
            dialogInstance.kapat();
        }
    }

    /**
     * Düzenleme modunu başlat - Sağ tıklanan noktayı düzenle
     */
    duzenlemeBaslat(nokta: FikoSmartPoint3D): void {
        this.ngZone.run(() => {
            this.duzenlemeModu = true;
            this.duzenlenenNokta = nokta;

            // Mevcut değerleri form'a yükle
            const config = nokta.getConfig();
            this.baslik = config.baslik;
            this.aciklama = config.aciklama || '';
            this.kategori = config.kategori;
            this.customIkon = config.customIkon || '';
            this.ozelRenk = config.ozelRenk || '';
            this.yaziRenk = config.yaziRenk || '';
            this.boyut = config.boyut;
            this.pencereBoyut = config.pencereBoyut;
            this.sabitBoyut = config.sabitBoyut;
            this.etiketGorunsun = config.etiketGorunsun;
            this.animasyonAktif = config.animasyonAktif;
            this.noktaPozisyonu = nokta.position.clone();

            // Profesyonel CAD özelliklerini yükle
            // PRO-only mod
            this.proOnlyMod = config.proOnlyMod ?? false;

            // Leader Line
            this.leaderAktif = config.leader?.aktif ?? false;
            this.leaderTip = config.leader?.tip ?? 'straight';
            this.leaderRenk = config.leader?.renk ?? '#1A1A1A';
            this.leaderKalinlik = config.leader?.kalinlik ?? 1.5;

            // GD&T
            this.gdtAktif = config.gdt?.aktif ?? false;
            this.gdtTip = config.gdt?.tip ?? 'position';
            this.gdtDeger = config.gdt?.deger ?? '';
            this.gdtDatum = config.gdt?.datum ?? '';
            this.gdtMalzemeKosulu = config.gdt?.malzemeKosulu ?? '';
            this.gdtSeffafArkaplan = config.gdt?.seffafArkaplan ?? true;
            this.gdtOffsetX = config.gdt?.offset?.x ?? 3;
            this.gdtOffsetY = config.gdt?.offset?.y ?? 1.5;
            this.gdtDuzlem = config.gdt?.duzlem ?? 'XY';

            // Yüzey Kalitesi
            this.yuzeyKaliteAktif = config.yuzeyKalite?.aktif ?? false;
            this.yuzeyKaliteTip = config.yuzeyKalite?.tip ?? 'machined';
            this.yuzeyRa = config.yuzeyKalite?.ra ?? 3.2;
            this.yuzeySeffafArkaplan = config.yuzeyKalite?.seffafArkaplan ?? true;
            this.yuzeyOffsetX = config.yuzeyKalite?.offset?.x ?? 2.5;
            this.yuzeyOffsetY = config.yuzeyKalite?.offset?.y ?? -1.2;
            this.yuzeyDuzlem = config.yuzeyKalite?.duzlem ?? 'XY';

            // Kaynak Sembolü
            this.kaynakAktif = config.kaynak?.aktif ?? false;
            this.kaynakTip = config.kaynak?.tip ?? 'fillet';
            this.kaynakBoyut = config.kaynak?.boyut ?? '';
            this.kaynakTamCevre = config.kaynak?.tamCevre ?? false;
            this.kaynakSahada = config.kaynak?.sahada ?? false;
            this.kaynakSeffafArkaplan = config.kaynak?.seffafArkaplan ?? true;
            this.kaynakOffsetX = config.kaynak?.offset?.x ?? -2.5;
            this.kaynakOffsetY = config.kaynak?.offset?.y ?? 1.2;
            this.kaynakDuzlem = config.kaynak?.duzlem ?? 'XY';

            // Datum Marker
            this.datumAktif = config.datum?.aktif ?? false;
            this.datumTip = config.datum?.tip ?? 'A';
            this.datumCustomLabel = config.datum?.customLabel ?? '';
            this.datumRenk = config.datum?.renk ?? '#1A1A1A';
            this.datumUcgenGoster = config.datum?.ucgenGoster ?? true;
            this.datumSeffafArkaplan = config.datum?.seffafArkaplan ?? true;
            this.datumOffsetX = config.datum?.offset?.x ?? -4;
            this.datumOffsetY = config.datum?.offset?.y ?? 0;
            this.datumDuzlem = config.datum?.duzlem ?? 'XY';

            // Dimension Line
            this.dimensionAktif = config.dimension?.aktif ?? false;
            this.dimensionTip = config.dimension?.tip ?? 'linear';
            this.dimensionDeger = config.dimension?.deger ?? '';
            this.dimensionToleransUst = config.dimension?.toleransUst ?? '';
            this.dimensionToleransAlt = config.dimension?.toleransAlt ?? '';
            this.dimensionBirim = config.dimension?.birim ?? 'mm';
            this.dimensionOkTip = config.dimension?.okTip ?? 'arrow';
            this.dimensionSeffafArkaplan = config.dimension?.seffafArkaplan ?? true;
            this.dimensionOffsetX = config.dimension?.offset?.x ?? 0;
            this.dimensionOffsetY = config.dimension?.offset?.y ?? -3;
            this.dimensionUzunluk = config.dimension?.uzunluk ?? 8;
            this.dimensionDuzlem = config.dimension?.duzlem ?? 'XY';

            // Kamera konumu durumunu güncelle
            this.kameraKonumuVar = nokta.hasKameraKonumu();

            this.cdr.detectChanges();
        });
    }

    /**
     * Düzenlemeyi kaydet
     */
    duzenlemeKaydet(): void {
        if (!this.duzenlenenNokta) return;

        // Profesyonel CAD config
        const proConfig = this.buildProConfig();

        // Config'i güncelle
        this.duzenlenenNokta.updateConfig({
            baslik: this.baslik,
            aciklama: this.aciklama,
            kategori: this.kategori,
            customIkon: this.customIkon,
            ozelRenk: this.ozelRenk,
            yaziRenk: this.yaziRenk,
            boyut: this.boyut,
            pencereBoyut: this.pencereBoyut,
            sabitBoyut: this.sabitBoyut,
            etiketGorunsun: this.etiketGorunsun,
            animasyonAktif: this.animasyonAktif,
            ...proConfig
        });

        // FikoEtiketler'deki bilgileri de güncelle (ürün ağacı senkronizasyonu)
        const fikoGrupRef = this.duzenlenenNokta.fikoGrupRef;
        if (fikoGrupRef) {
            fikoGrupRef.name = this.baslik;
            if (fikoGrupRef.userData['FikoEtiket']) {
                fikoGrupRef.userData['FikoEtiket'].baslik = this.baslik;
                fikoGrupRef.userData['FikoEtiket'].aciklama = this.aciklama;
                fikoGrupRef.userData['FikoEtiket'].kategori = this.kategori;
                fikoGrupRef.userData['FikoEtiket'].customIkon = this.customIkon;
                fikoGrupRef.userData['FikoEtiket'].ozelRenk = this.ozelRenk;
                fikoGrupRef.userData['FikoEtiket'].yaziRenk = this.yaziRenk;
                fikoGrupRef.userData['FikoEtiket'].boyut = this.boyut;
                fikoGrupRef.userData['FikoEtiket'].pencereBoyut = this.pencereBoyut;
                // Profesyonel CAD özellikleri
                fikoGrupRef.userData['FikoEtiket'].leader = proConfig.leader;
                fikoGrupRef.userData['FikoEtiket'].gdt = proConfig.gdt;
                fikoGrupRef.userData['FikoEtiket'].yuzeyKalite = proConfig.yuzeyKalite;
                fikoGrupRef.userData['FikoEtiket'].kaynak = proConfig.kaynak;
                if (this.noktaPozisyonu) {
                    fikoGrupRef.userData['FikoEtiket'].pozisyon = {
                        x: this.noktaPozisyonu.x,
                        y: this.noktaPozisyonu.y,
                        z: this.noktaPozisyonu.z
                    };
                }
            }
        }

        this.anaGosterim?.ciz();
        this.anaGosterim?.FikoYT?.degisti?.emit();

        this.duzenlemeyiIptal();
    }

    /**
     * Düzenlemeyi iptal et
     */
    duzenlemeyiIptal(): void {
        this.duzenlemeModu = false;
        this.duzenlenenNokta = null;

        // Formu sıfırla
        this.baslik = 'Nokta';
        this.aciklama = '';
        this.kategori = 'info';
        this.customIkon = '';
        this.ozelRenk = '';
        this.yaziRenk = '';
        this.boyut = 48;
        this.pencereBoyut = 100;
        this.sabitBoyut = true;
        this.etiketGorunsun = false;
        this.animasyonAktif = true;
        this.noktaPozisyonu = null;

        // Profesyonel CAD özelliklerini sıfırla
        // PRO-only mod
        this.proOnlyMod = false;

        // Leader Line
        this.leaderAktif = false;
        this.leaderTip = 'straight';
        this.leaderRenk = '#1A1A1A';
        this.leaderKalinlik = 1.5;

        // GD&T
        this.gdtAktif = false;
        this.gdtTip = 'position';
        this.gdtDeger = '';
        this.gdtDatum = '';
        this.gdtMalzemeKosulu = '';

        // Yüzey Kalitesi
        this.yuzeyKaliteAktif = false;
        this.yuzeyKaliteTip = 'machined';
        this.yuzeyRa = 3.2;

        // Kaynak
        this.kaynakAktif = false;
        this.kaynakTip = 'fillet';
        this.kaynakBoyut = '';
        this.kaynakTamCevre = false;
        this.kaynakSahada = false;

        // Datum Marker
        this.datumAktif = false;
        this.datumTip = 'A';
        this.datumCustomLabel = '';
        this.datumRenk = '#1A1A1A';
        this.datumUcgenGoster = true;

        // Dimension Line
        this.dimensionAktif = false;
        this.dimensionTip = 'linear';
        this.dimensionDeger = '';
        this.dimensionToleransUst = '';
        this.dimensionToleransAlt = '';
        this.dimensionBirim = 'mm';
        this.dimensionOkTip = 'arrow';

        this.cdr.detectChanges();
    }

    /**
     * Düzenlenen noktayı sil
     */
    noktaSil(): void {
        if (!this.duzenlenenNokta) return;

        const sahne = this.anaGosterim?.sahneler?.[0];
        if (sahne?.userData['smartPoints3D']) {
            const index = sahne.userData['smartPoints3D'].indexOf(this.duzenlenenNokta);
            if (index > -1) {
                sahne.userData['smartPoints3D'].splice(index, 1);
            }
        }

        // FikoEtiketler'den de kaldır (ürün ağacından)
        const fikoGrupRef = this.duzenlenenNokta.fikoGrupRef;
        if (fikoGrupRef && fikoGrupRef.parent) {
            fikoGrupRef.parent.remove(fikoGrupRef);
        }

        // Noktayı sahne'den kaldır
        this.duzenlenenNokta.dispose();

        this.anaGosterim?.ciz();
        this.anaGosterim?.FikoYT?.degisti?.emit();

        this.duzenlemeyiIptal();
    }

    /**
     * Mevcut kamera konumunu noktaya kaydet
     */
    kameraKonumuKaydet(): void {
        const nokta = this.duzenlemeModu ? this.duzenlenenNokta : this.mevcutNokta;
        if (!nokta) return;

        const konum = nokta.kameraKonumuKaydet();
        if (konum) {
            this.kameraKonumuVar = true;
            this.cdr.detectChanges();
        }
    }

    /**
     * Kaydedilen kamera konumuna git
     */
    async kameraKonumunaGit(): Promise<void> {
        const nokta = this.duzenlemeModu ? this.duzenlenenNokta : this.mevcutNokta;
        if (!nokta || !nokta.hasKameraKonumu()) return;

        await nokta.kameraKonumunaGit(750);
        this.anaGosterim?.ciz();
    }

    /**
     * Kamera konumunu temizle
     */
    kameraKonumuTemizle(): void {
        const nokta = this.duzenlemeModu ? this.duzenlenenNokta : this.mevcutNokta;
        if (!nokta) return;

        nokta.kameraKonumuTemizle();
        this.kameraKonumuVar = false;
        this.cdr.detectChanges();
    }
}
